import { useState } from 'react';
import { Filter, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { AlarmFilters as AlarmFiltersType, AlarmStatus, AlarmSeverity, AlarmType } from '@/types/api';
import { controllersService } from '@/services';
import { useQuery } from '@tanstack/react-query';

interface AlarmFiltersProps {
  filters: AlarmFiltersType;
  onFiltersChange: (filters: AlarmFiltersType) => void;
  onReset: () => void;
}

const alarmStatuses: { value: AlarmStatus; label: string }[] = [
  { value: 'active', label: 'Activa' },
  { value: 'acknowledged', label: 'Reconocida' },
  { value: 'resolved', label: 'Resuelta' },
];

const alarmSeverities: { value: AlarmSeverity; label: string }[] = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Advertencia' },
  { value: 'alarm', label: 'Alarma' },
  { value: 'critical', label: 'Crítica' },
];

const alarmTypes: { value: AlarmType; label: string }[] = [
  { value: 'irrigation_missed', label: 'Riego perdido' },
  { value: 'irrigation_out_of_hours', label: 'Riego fuera de hora' },
  { value: 'supervision_started', label: 'Supervisión iniciada' },
  { value: 'supervision_stopped', label: 'Supervisión detenida' },
  { value: 'time_sync_failed', label: 'Fallo sincronización hora' },
  { value: 'time_sync_corrected', label: 'Hora corregida' },
  { value: 'device_offline', label: 'Dispositivo offline' },
  { value: 'device_back_online', label: 'Dispositivo online' },
  { value: 'schedule_sync_failed', label: 'Fallo sincronización programación' },
  { value: 'schedule_conflict', label: 'Conflicto de programaciones' },
  { value: 'schedule_mismatch', label: 'Programación no coincide' },
  { value: 'schedule_missing_device', label: 'Programación falta en dispositivo' },
  { value: 'schedule_extra_device', label: 'Programación extra en dispositivo' },
  { value: 'schedule_verification_err', label: 'Error verificación programación' },
];

export function AlarmFilters({ filters, onFiltersChange, onReset }: AlarmFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Obtener controladoras para el filtro
  const { data: controllersData } = useQuery({
    queryKey: ['controllers-for-filter'],
    queryFn: async () => {
      const response = await controllersService.getAll({ pageSize: 100 });
      if (!response.success) return { items: [] };
      return response.data;
    },
  });

  const controllers = controllersData?.items || [];

  const activeFiltersCount = [
    filters.status,
    filters.severity,
    filters.type,
    filters.controllerId,
    filters.startDate,
    filters.endDate,
  ].filter(Boolean).length;

  const handleFilterChange = (key: keyof AlarmFiltersType, value: string | undefined) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
      page: 1, // Reset a primera página al cambiar filtros
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
          className="gap-2"
        >
          <Filter className="w-4 h-4" />
          Filtros
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="ml-1">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
        {activeFiltersCount > 0 && (
          <Button variant="ghost" size="sm" onClick={onReset} className="gap-2">
            <X className="w-4 h-4" />
            Limpiar
          </Button>
        )}
      </div>

      {isOpen && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtros de Alarmas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Estado */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Estado</label>
              <div className="relative">
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                  className={cn(
                    "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 appearance-none cursor-pointer",
                    !filters.status && "text-muted-foreground"
                  )}
                >
                  <option value="">Todos</option>
                  {alarmStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
              </div>
            </div>

            {/* Severidad */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Severidad</label>
              <div className="relative">
                <select
                  value={filters.severity || ''}
                  onChange={(e) => handleFilterChange('severity', e.target.value || undefined)}
                  className={cn(
                    "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 appearance-none cursor-pointer",
                    !filters.severity && "text-muted-foreground"
                  )}
                >
                  <option value="">Todas</option>
                  {alarmSeverities.map((severity) => (
                    <option key={severity.value} value={severity.value}>
                      {severity.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
              </div>
            </div>

            {/* Tipo */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo</label>
              <div className="relative">
                <select
                  value={filters.type || ''}
                  onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                  className={cn(
                    "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 appearance-none cursor-pointer",
                    !filters.type && "text-muted-foreground"
                  )}
                >
                  <option value="">Todos</option>
                  {alarmTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
              </div>
            </div>

            {/* Controladora */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Controladora</label>
              <div className="relative">
                <select
                  value={filters.controllerId || ''}
                  onChange={(e) => handleFilterChange('controllerId', e.target.value || undefined)}
                  className={cn(
                    "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 appearance-none cursor-pointer",
                    !filters.controllerId && "text-muted-foreground"
                  )}
                >
                  <option value="">Todas</option>
                  {controllers.map((controller) => (
                    <option key={controller.id} value={controller.id}>
                      {controller.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
              </div>
            </div>

            {/* Fechas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Fecha inicio</label>
                <Input
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => handleFilterChange('startDate', e.target.value || undefined)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Fecha fin</label>
                <Input
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => handleFilterChange('endDate', e.target.value || undefined)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
